<script setup>
/* Auswahlfeld für die Nationalität: Eintippen filtert, obere Gruppe afrikanische Staaten, darunter weitere
 * Länder (Daten und Suche: domain/countries.js). Der gespeicherte Wert ist der ENGLISCHE Ländername;
 * angezeigt wird der Name in der Sprache der App. Ein Altwert, der nicht in der Liste steht, erscheint
 * als eigener Eintrag und bleibt erhalten, bis etwas anderes gewählt wird.
 *
 * Aussehen: das Eingabefeld erbt die Gestaltung von ".field input" (main.css), die Liste ist wie das
 * Ausklappmenü der App gestaltet. Bedienung per Tastatur: Pfeile, Enter, Esc.
 */

import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { optionsFor, displayValue } from '../domain/countries.js';

const model = defineModel({ type: String, default: '' });
defineProps({
  id: { type: String, default: undefined },
});

const { t, locale } = useI18n();

const root = ref(null);
const input = ref(null);
const open = ref(false);
const query = ref('');
const active = ref(-1);

const groups = computed(() => optionsFor({ value: model.value, query: query.value, locale: locale.value }));
/* Flache Liste für die Tastatur: erst der Eintrag zum Leeren (nur ohne Suchtext), dann alle Länder */
const flat = computed(() => {
  const list = [];
  if (!query.value && model.value) list.push({ value: '', label: '—', clear: true });
  for (const g of groups.value) list.push(...g.items);
  return list;
});
const indexOf = (item) => flat.value.findIndex((o) => o.value === item.value && !!o.clear === !!item.clear);

const shown = computed(() => displayValue(model.value, locale.value));
const listId = `cs-list-${Math.random().toString(36).slice(2, 8)}`;

function openList() {
  if (open.value) return;
  open.value = true;
  query.value = '';
  active.value = Math.max(0, flat.value.findIndex((o) => o.value === model.value && !o.clear));
  nextTick(scrollActive);
}
function closeList() {
  open.value = false;
  query.value = '';
}
function choose(item) {
  model.value = item.value;
  closeList();
}
function scrollActive() {
  root.value?.querySelector('.cs-opt.active')?.scrollIntoView({ block: 'nearest' });
}
function move(delta) {
  if (!open.value) return openList();
  const n = flat.value.length;
  if (!n) return;
  active.value = (active.value + delta + n) % n;
  nextTick(scrollActive);
}
function onEnter(e) {
  if (!open.value) return;
  e.preventDefault();
  const item = flat.value[active.value];
  if (item) choose(item);
}
function onEsc(e) {
  if (!open.value) return;
  e.preventDefault();
  e.stopPropagation(); // schließt nur die Liste, nicht den umgebenden Dialog
  closeList();
}
function onInput(e) {
  query.value = e.target.value;
  if (!open.value) open.value = true;
  active.value = flat.value.length ? 0 : -1;
}
watch(query, () => { if (active.value >= flat.value.length) active.value = flat.value.length - 1; });

function onDocumentDown(e) {
  if (open.value && root.value && !root.value.contains(e.target)) closeList();
}
onMounted(() => document.addEventListener('mousedown', onDocumentDown));
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocumentDown));
</script>

<template>
  <div ref="root" class="cs">
    <input
      :id="id"
      ref="input"
      type="text"
      role="combobox"
      autocomplete="off"
      spellcheck="false"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="listId"
      aria-autocomplete="list"
      :aria-activedescendant="open && active >= 0 ? `${listId}-${active}` : undefined"
      :value="open ? query : shown"
      :placeholder="open ? (shown || $t('country.search')) : ''"
      @focus="openList"
      @click="openList"
      @input="onInput"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter="onEnter"
      @keydown.esc="onEsc"
      @keydown.tab="closeList"
    />
    <span class="cs-caret" aria-hidden="true">▾</span>

    <ul v-if="open" :id="listId" class="cs-list" role="listbox">
      <li v-if="flat.length === 0" class="cs-empty">{{ $t('country.noResults') }}</li>
      <template v-else>
        <li
          v-if="!query && model"
          :id="`${listId}-0`"
          class="cs-opt cs-clear"
          :class="{ active: active === 0 }"
          role="option"
          @mousedown.prevent="choose({ value: '' })"
          @mousemove="active = 0"
        >—</li>
        <template v-for="g in groups" :key="g.group">
          <li class="cs-group" role="presentation">{{ $t(`country.group.${g.group}`) }}</li>
          <li
            v-for="o in g.items"
            :id="`${listId}-${indexOf(o)}`"
            :key="g.group + o.value"
            class="cs-opt"
            :class="{ active: indexOf(o) === active, selected: o.value === model }"
            role="option"
            :aria-selected="o.value === model ? 'true' : 'false'"
            @mousedown.prevent="choose(o)"
            @mousemove="active = indexOf(o)"
          >{{ o.label }}</li>
        </template>
      </template>
    </ul>
  </div>
</template>

<style scoped>
.cs {
  position: relative;
  display: flex;
  flex-direction: column;
}
.cs input {
  width: 100%;
  padding-right: 28px;
}
.cs-caret {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--dim);
  pointer-events: none;
}
.cs-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 40;
  margin: 4px 0 0;
  padding: 4px 0;
  list-style: none;
  max-height: min(320px, 50vh);
  overflow-y: auto;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 8px 24px var(--shadow-soft);
  text-align: left;
}
.cs-group {
  padding: 8px 12px 4px;
  font-size: 0.64rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dim);
}
.cs-opt {
  padding: 8px 12px;
  font-size: 0.86rem;
  color: var(--text);
  cursor: pointer;
}
.cs-opt.active {
  background: var(--card-hover);
}
.cs-opt.selected {
  color: var(--green);
}
.cs-empty {
  padding: 10px 12px;
  font-size: 0.82rem;
  color: var(--muted);
}
</style>
